import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  LinearProgress,
  Link,
  Tooltip,
  IconButton,
  Chip,
  Typography,
  Divider,
  Stack,
  Autocomplete,
  TextField,
} from "@mui/material";

import MoreVertIcon from "@mui/icons-material/MoreVert";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";

import { useSnackbar } from "../../../../contexts/SnackbarContext";
import { useOrganization } from "..";
import { useAuth } from "../../../../contexts/AuthContext";

import { fetchData } from "../../../../fetchData";

type IntegraProductCode = "essencial" | "saveRH" | "saveFinanc";

type PersonRow = {
  _id: string;
  name: string;
  login: string;
  status: boolean;
};

const integraProducts: { key: IntegraProductCode; label: string }[] = [
  { key: "essencial", label: "Essencial" },
  { key: "saveRH", label: "SAVE RH" },
  { key: "saveFinanc", label: "SAVE Financeiro" },
];

const getIntegraProductLabel = (product: IntegraProductCode) =>
  integraProducts.find((item) => item.key === product)?.label ?? product;

const toIdString = (value: unknown): string | null => {
  if (!value) return null;
  if (typeof value === "object" && value !== null && "_id" in value) {
    return String((value as { _id: unknown })._id);
  }
  return String(value);
};

const OrganizationPersonsTab = () => {
  const navigate = useNavigate();
  const snackbar = useSnackbar();
  const auth = useAuth();
  const { data, set } = useOrganization();
  const [progress, setProgress] = useState(true);
  const [persons, setPersons] = useState<PersonRow[]>([]);
  const [savingProduct, setSavingProduct] = useState<IntegraProductCode | null>(
    null,
  );

  const activeIntegraProducts = useMemo(
    () =>
      integraProducts.filter((product) => data.products?.[product.key]?.active),
    [data.products],
  );

  const activePersons = useMemo(
    () => persons.filter((person) => person.status),
    [persons],
  );

  const init = async () => {
    setProgress(true);
    try {
      const result = await fetchData(
        `/users?is_admin=false&client=${data.reference}&sort=name&limit=100&skip=0`,
        {
          method: "GET",
        },
      );

      setPersons(result.rows);
    } catch {
      setPersons([]);
    }

    setProgress(false);
  };

  useEffect(() => {
    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data._id]);

  const handleAddLegalResponsible = async (personId: string) => {
    try {
      await fetchData(`/organizations/${data._id}/legalUser`, {
        method: "POST",
        body: JSON.stringify({
          _id: personId,
        }),
      });

      set({ ...data, legal_responsible: personId });

      snackbar?.open("Responsável legal atualizado com sucesso!", "success");
    } catch {
      snackbar?.open(
        "Ops! No momento não foi possível atualizar o responsável legal!",
        "error",
      );
    }
  };

  const handleProductResponsibleChange = async (
    person: PersonRow | null,
    product: IntegraProductCode,
  ) => {
    const productLabel = getIntegraProductLabel(product);

    setSavingProduct(product);
    try {
      const updated = await fetchData(
        `/organizations/${data._id}/productUser`,
        {
          method: "POST",
          body: JSON.stringify({
            product,
            _id: person?._id ?? null,
          }),
        },
      );

      set(updated);

      snackbar?.open(
        person
          ? `Responsável do produto ${productLabel} atualizado com sucesso!`
          : `Responsável do produto ${productLabel} removido com sucesso!`,
        "success",
      );
    } catch {
      snackbar?.open(
        person
          ? `Ops! No momento não foi possível atualizar o responsável do produto ${productLabel}!`
          : `Ops! No momento não foi possível remover o responsável do produto ${productLabel}!`,
        "error",
      );
    } finally {
      setSavingProduct(null);
    }
  };

  const isManager = auth?.user.permission_level === "manager";

  if (progress) {
    return <LinearProgress sx={{ m: 2 }} />;
  }

  return (
    <Box sx={{ p: 2 }}>
      {activeIntegraProducts.length > 0 && (
        <Box sx={{ mb: 2 }}>
          <Stack
            direction="row"
            spacing={0.5}
            alignItems="center"
            sx={{ mb: 1.5 }}
          >
            <Typography
              variant="caption"
              color="text.secondary"
              fontWeight="bold"
            >
              Responsáveis por produto
            </Typography>
            <Tooltip
              title="O usuário informado receberá as pesquisas do CES para os produtos informados."
              arrow
              placement="top"
            >
              <IconButton
                size="small"
                sx={{ p: 0.25, color: "text.secondary" }}
              >
                <HelpOutlineIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
          <Stack direction="row" spacing={2} sx={{ width: "100%" }}>
            {activeIntegraProducts.map((product) => {
              const responsibleId = toIdString(
                data.products?.[product.key]?.user_responsible,
              );
              const selectedPerson =
                activePersons.find(
                  (person) => String(person._id) === String(responsibleId),
                ) ?? null;

              return (
                <Autocomplete
                  key={product.key}
                  size="small"
                  sx={{ flex: 1, minWidth: 0 }}
                  disabled={!isManager || savingProduct === product.key}
                  options={activePersons}
                  value={selectedPerson}
                  getOptionLabel={(option) => option.name}
                  isOptionEqualToValue={(option, value) =>
                    option._id === value._id
                  }
                  onChange={(_, value) => {
                    handleProductResponsibleChange(value, product.key);
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={product.label}
                      placeholder="Selecione o contato"
                    />
                  )}
                />
              );
            })}
          </Stack>
          <Divider sx={{ mt: 2 }} />
        </Box>
      )}

      {persons.length > 0 && (
        <TableContainer sx={{ borderBottom: 1, borderColor: "divider" }}>
          <Table size="small" aria-label="a dense table">
            <TableHead>
              <TableRow>
                <TableCell>Contato</TableCell>
                <TableCell width={80}>Usuário</TableCell>
                <TableCell width={80}>Situação</TableCell>
                {isManager && (
                  <TableCell align="center" width={80}>
                    <MoreVertIcon />
                  </TableCell>
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {persons.map((row) => (
                <TableRow
                  key={row._id}
                  sx={{
                    "&:last-child td, &:last-child th": {
                      border: 0,
                    },
                    backgroundColor: !row.status
                      ? "rgba(255, 99, 71,.2)"
                      : "initial",
                  }}
                >
                  <TableCell component="th" scope="row">
                    <Box
                      sx={{
                        display: "flex",
                        flexWrap: "wrap",
                        alignItems: "center",
                      }}
                    >
                      <Link
                        onClick={() => {
                          navigate(`/persons/${row._id}`);
                        }}
                        sx={{
                          cursor: "pointer",
                        }}
                      >
                        {row.name}
                      </Link>
                      {row.status && data.legal_responsible === row._id && (
                        <Chip
                          size="small"
                          label="Responsável Legal"
                          sx={{ ml: 1 }}
                        />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>{row.login}</TableCell>
                  <TableCell>{row.status ? "Ativo" : "Inativo"}</TableCell>
                  {isManager && (
                    <TableCell align="center">
                      {row.status && data.legal_responsible !== row._id && (
                        <Tooltip title="Marcar Responsável Legal">
                          <IconButton
                            color="primary"
                            size="small"
                            onClick={() => {
                              handleAddLegalResponsible(row._id);
                            }}
                          >
                            <AdminPanelSettingsIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

export default OrganizationPersonsTab;
